import { Button, Stack, Typography } from '@mui/material'
import xor from 'lodash/xor'
import { FC, useCallback, useEffect, useState } from 'react'
import { IconButton, InformationModal } from 'utoniq-ui'

import {
  ArtistPreSaleLotteryApplicantsConnectionFragment,
  Maybe,
  PreSaleItemFilterFragment,
  PreSaleLotteryApplicantFilterInputs,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, BottomSafeArea } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { LotteryApplicantFilterCell } from './LotteryApplicantFilterCell'
import { LotteryApplicantTableCell } from './LotteryApplicantTableCell'

type LotteryApplicantEditModalProps = {
  artistId: string
  open: boolean
  loading: boolean
  hasNextPage: boolean
  updatingPreSaleLotteryApplicantStatus: boolean
  maxChoice: number
  items: Maybe<PreSaleItemFilterFragment[]>
  lotteryApplicants: Maybe<ArtistPreSaleLotteryApplicantsConnectionFragment>
  onClose: () => void
  filters: PreSaleLotteryApplicantFilterInputs
  onFilters: (filters: PreSaleLotteryApplicantFilterInputs) => Promise<void>
  onSelectedWinner: (item: string[]) => Promise<void>
  onFetchMore: () => Promise<void>
}

export const LotteryApplicantEditModal: FC<LotteryApplicantEditModalProps> = ({
  artistId,
  open,
  loading,
  hasNextPage,
  maxChoice,
  items,
  filters,
  lotteryApplicants,
  updatingPreSaleLotteryApplicantStatus,
  onClose,
  onFilters,
  onSelectedWinner,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const [selectedWinners, setSelectedWinners] = useState<string[]>([])

  const handleSelectAll = useCallback(() => {}, [])

  useEffect(() => {
    if (lotteryApplicants?.edges.length) {
      setSelectedWinners(
        lotteryApplicants.edges
          .map(({ node }) =>
            node.winChoiceNumber ? `${node.id}:${node.winChoiceNumber}` : ''
          )
          .filter(o => !!o)
      )
    }
  }, [lotteryApplicants])

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <ArtistThemeProvider artistId={artistId}>
        <BaseLayout
          margin
          header={{
            left: (
              <IconButton.Close
                edge="start"
                size="medium"
                onClick={onClose}
                sx={{ 'svg, svg:hover': { color: 'white' } }}
              />
            ),
            title: t('choiceTitle', { number: selectedWinners.length }),
            right: (
              <Typography
                variant="h6"
                width={80}
                onClick={handleSelectAll}
                color="white"
              >
                {t('selectAll', 'すべて選択')}
              </Typography>
            ),
          }}
          customNavigation={
            <FixedBottomCell
              disabled={!selectedWinners.length}
              selectedCount={selectedWinners.length}
              updatingPreSaleLotteryApplicantStatus={
                updatingPreSaleLotteryApplicantStatus
              }
              onSelectedWinner={async () => {
                await onSelectedWinner(selectedWinners)
                onClose()
              }}
            />
          }
        >
          <LotteryApplicantFilterCell
            maxChoice={maxChoice}
            items={items}
            filters={filters}
            onFilters={onFilters}
          />
          <LotteryApplicantTableCell
            isEditable
            loading={loading}
            hasNextPage={hasNextPage}
            selectedWinners={selectedWinners}
            lotteryApplicants={lotteryApplicants}
            onSelectedWinner={(id, userId) =>
              setSelectedWinners(prevState =>
                xor(prevState, [
                  id,
                  ...prevState.filter(selectedWinner =>
                    selectedWinner.includes(userId)
                  ),
                ])
              )
            }
            onFetchMore={onFetchMore}
          />
        </BaseLayout>
      </ArtistThemeProvider>
    </FullscreenModal>
  )
}

type FixedBottomCellProps = {
  disabled: boolean
  updatingPreSaleLotteryApplicantStatus: boolean
  selectedCount: number
  onSelectedWinner: () => Promise<void>
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  disabled,
  updatingPreSaleLotteryApplicantStatus,
  selectedCount,
  onSelectedWinner,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          disabled={disabled}
          onClick={() => setOpenConfirm(true)}
        >
          {t('decidingWinner', '当選者を決定する')}
        </Button>
      </CellFrame>
      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('decidingWinnerTitle', '当選予定者に決定します。')}
        content={
          <Stack spacing={3} pb={7} sx={{ textAlign: 'left' }}>
            <Typography variant="body2">
              {t(
                'decidingWinnerDesc',
                '当選予定者は後ほど変更することもできます。'
              )}
            </Typography>
            <Stack>
              <Typography variant="subtitle1">
                {t('winnerNumber', '当選人数')}
              </Typography>
              <Typography variant="body2">
                {t('peopleCount', { number: selectedCount })}
              </Typography>
            </Stack>
          </Stack>
        }
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            loading={updatingPreSaleLotteryApplicantStatus}
            disabled={updatingPreSaleLotteryApplicantStatus}
            onClick={async () => {
              await onSelectedWinner()
              setOpenConfirm(false)
            }}
          >
            {t('confirm', '確定する')}
          </Button>
        }
        cancelText={t('close')}
      />
    </BottomSafeArea>
  )
}
